import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    Grid,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Container,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Chip,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Stack,
    Divider,
    useTheme,
    useMediaQuery,
    Card,
    CardContent
} from "@mui/material";
import {
    Edit as EditIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    FilterAlt as FilterIcon,
    History as HistoryIcon,
    Timer as TimerIcon,
    AccessTime as AccessTimeIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon
} from "@mui/icons-material";

import { getTimeLogs, punchTime, deleteTimeLog } from "../../hooks/apiHandler";

/**
 * UTILITY: Get YYYY-MM-DD in local time
 */
const getLocalDateString = (date = new Date()) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/**
 * Simple Calendar Component for Sidebar
 */
const SimpleCalendar = ({ selectedDate, onDateChange }) => {
    const [viewDate, setViewDate] = useState(new Date());
    const daysOfWeek = ["S", "M", "T", "W", "T", "F", "S"];

    const startOfMonth = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
    const endOfMonth = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0);
    const startDay = startOfMonth.getDay();
    const daysInMonth = endOfMonth.getDate();

    const handlePrevMonth = () => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
    const handleNextMonth = () => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));

    const isSelected = (day) => {
        const d = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
        return getLocalDateString(d) === selectedDate;
    };

    const isToday = (day) => {
        const d = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
        return getLocalDateString(d) === getLocalDateString(new Date());
    };

    const calendarDays = [];
    for (let i = 0; i < startDay; i++) calendarDays.push(null);
    for (let i = 1; i <= daysInMonth; i++) calendarDays.push(i);

    return (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                    {viewDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
                </Typography>
                <Box>
                    <IconButton size="small" onClick={handlePrevMonth}><ChevronLeftIcon /></IconButton>
                    <IconButton size="small" onClick={handleNextMonth}><ChevronRightIcon /></IconButton>
                </Box>
            </Stack>

            <Grid container spacing={1}>
                {daysOfWeek.map((day, idx) => (
                    <Grid item xs={1.71} key={`header-${idx}`} sx={{ textAlign: 'center' }}>
                        <Typography variant="caption" fontWeight="bold" color="text.secondary">{day}</Typography>
                    </Grid>
                ))}
                {calendarDays.map((day, idx) => (
                    <Grid item xs={1.71} key={`day-${idx}`} sx={{ textAlign: 'center' }}>
                        {day ? (
                            <Box
                                onClick={() => {
                                    const d = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
                                    onDateChange(getLocalDateString(d));
                                }}
                                sx={{
                                    width: 28,
                                    height: 28,
                                    lineHeight: '28px',
                                    m: 'auto',
                                    borderRadius: '50%',
                                    cursor: 'pointer',
                                    fontSize: '0.75rem',
                                    bgcolor: isSelected(day) ? 'primary.main' : 'transparent',
                                    color: isSelected(day) ? 'white' : (isToday(day) ? 'primary.main' : 'text.primary'),
                                    fontWeight: isToday(day) || isSelected(day) ? 'bold' : 'normal',
                                    '&:hover': { bgcolor: isSelected(day) ? 'primary.dark' : 'grey.100' }
                                }}
                            >
                                {day}
                            </Box>
                        ) : null}
                    </Grid>
                ))}
            </Grid>
        </Paper>
    );
};

/**
 * TimePunchManager Component
 */
export const TimePunchManager = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    // const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));

    const [loading, setLoading] = useState(false);
    const [logs, setLogs] = useState([]);
    const [openModal, setOpenModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [staffMembers, setStaffMembers] = useState([]);

    const initialFormData = {
        _id: null,
        userID: "",
        userName: "",
        startTime: "",
        endTime: "",
        note: ""
    };

    const [formData, setFormData] = useState(initialFormData);
    const [dailySummary, setDailySummary] = useState({ staffCount: 0, totalHours: "0.00" });

    const [filters, setFilters] = useState({
        search: "",
        dateFrom: getLocalDateString(new Date())
    });

    const storeID = (() => {
        try {
            const ds = window.localStorage.getItem('ds');
            return ds ? JSON.parse(ds) : "default-store";
        } catch (e) {
            return "default-store";
        }
    })();

    const formatDateForInput = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return "";
        
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };

    const calculateHours = (start, end) => {
        if (!start || !end) return "0.00";
        const diff = new Date(end) - new Date(start);
        if (isNaN(diff)) return "0.00";
        return (diff / (1000 * 60 * 60)).toFixed(2);
    };

    const calculateHoursAndUsersCount = (logData) => {
        const uniqueUsers = new Set();
        let total = 0;

        logData.forEach(log => {
            const hours = calculateHours(log.startTime, log.endTime);
            total += parseFloat(hours);
            uniqueUsers.add(log.userID);
        });

        setDailySummary({ 
            staffCount: uniqueUsers.size, 
            totalHours: total.toFixed(2) 
        });
    };

    const fetchLogs = async () => {
        try {
            setLoading(true);
            const [year, month, day] = filters.dateFrom.split("-").map(Number);
            const startDate = new Date(year, month - 1, day, 0, 0, 0, 0);
            const endDate = new Date(year, month - 1, day, 23, 59, 59, 999);
            
            const response = await getTimeLogs({ storeID, startDate, endDate });
            if (response && response.data) {
                const logsReceived = response.data.timeLog || [];
                setLogs(logsReceived);
                setStaffMembers(response.data.users || []);
                calculateHoursAndUsersCount(logsReceived);
            }
        } catch (error) {
            console.error("Error fetching logs:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLogs();
    }, [filters.dateFrom]);

    const handleOpenModal = (log = null) => {
        if (log) {
            setEditMode(true);
            setFormData({
                _id: log._id,
                userID: log.userID,
                userName: staffMembers.find(u => u.userID === log.userID)?.name || "Unknown",
                startTime: formatDateForInput(log.startTime),
                endTime: log.endTime ? formatDateForInput(log.endTime) : "",
                note: log.note || ""
            });
        } else {
            setEditMode(false);
            setFormData(initialFormData);
        }
        setOpenModal(true);
    };

    const handleClose = () => setOpenModal(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            await punchTime({ storeID, timeLog: formData });
            fetchLogs();
            handleClose();
        } catch (error) {
            console.error("Error saving record:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Remove this time log?")) {
            try {
                await deleteTimeLog({ storeID, logID: id });
                setLogs(prev => prev.filter(l => l._id !== id));
            } catch (error) {
                console.error("Error deleting record:", error);
            }
        }
    };

    return (
        <Container maxWidth="xl" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1, sm: 2 } }}>
            <Grid container spacing={3}>
                <Grid item xs={12} lg={9}>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        spacing={2}
                        sx={{ mb: 3 }}
                    >
                        <Box>
                            <Typography variant={isMobile ? "h5" : "h4"} fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TimerIcon fontSize={isMobile ? "medium" : "large"} color="primary" /> Time Punch Manager
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Records for <strong>{filters.dateFrom}</strong>
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => handleOpenModal()}
                            fullWidth={isMobile}
                            sx={{ borderRadius: 2, px: 3 }}
                        >
                            Add Manual Punch
                        </Button>
                    </Stack>

                    <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={8}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Search Staff"
                                    placeholder="Enter name..."
                                    value={filters.search}
                                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={6} md={2}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="date"
                                    label="Date"
                                    InputLabelProps={{ shrink: true }}
                                    value={filters.dateFrom}
                                    onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={6} md={2}>
                                <Button fullWidth variant="outlined" startIcon={<FilterIcon />} onClick={fetchLogs}>
                                    Refresh
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>

                    {loading && !openModal ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>
                    ) : (
                        <>
                            {isMobile ? (
                                <Stack spacing={2}>
                                    {logs.filter(l => staffMembers.find(u => u.userID === l.userID)?.name.toLowerCase().includes(filters.search.toLowerCase())).map((log) => (
                                        <Card key={log._id} variant="outlined" sx={{ borderRadius: 2 }}>
                                            <CardContent sx={{ p: 2 }}>
                                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                                    <Box>
                                                        <Typography variant="subtitle1" fontWeight="bold">
                                                            {staffMembers.find(u => u.userID === log.userID)?.name || "Unknown"}
                                                        </Typography>
                                                        <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>ID: {log.userID}</Typography>
                                                    </Box>
                                                </Stack>
                                                <Divider sx={{ my: 1.5 }} />
                                                <Grid container spacing={2}>
                                                    <Grid item xs={6}>
                                                        <Typography variant="caption" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <AccessTimeIcon sx={{ fontSize: 14 }} /> Clock In
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {log.startTime ? new Date(log.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }) : "-"}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Typography variant="caption" color="textSecondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <AccessTimeIcon sx={{ fontSize: 14 }} /> Clock Out
                                                        </Typography>
                                                        {log.endTime ? (
                                                            <Typography variant="body2">
                                                                {new Date(log.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })}
                                                            </Typography>
                                                        ) : (
                                                            <Typography color="error" variant="body2" sx={{ fontStyle: 'italic' }}>Active</Typography>
                                                        )}
                                                    </Grid>
                                                </Grid>
                                                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <Chip label={`${calculateHours(log.startTime, log.endTime)} Hrs`} size="small" color="primary" />
                                                    <Box>
                                                        <IconButton size="small" color="primary" onClick={() => handleOpenModal(log)} sx={{ mr: 1 }}><EditIcon fontSize="small" /></IconButton>
                                                        <IconButton size="small" color="error" onClick={() => handleDelete(log._id)}><DeleteIcon fontSize="small" /></IconButton>
                                                    </Box>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </Stack>
                            ) : (
                                <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 2 }}>
                                    <Table stickyHeader>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Staff Member</TableCell>
                                                <TableCell>Clock In</TableCell>
                                                <TableCell>Clock Out</TableCell>
                                                <TableCell align="center">Hours</TableCell>
                                                <TableCell align="right">Actions</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {logs.filter(l => staffMembers.find(u => u.userID === l.userID)?.name.toLowerCase().includes(filters.search.toLowerCase())).map((log) => (
                                                <TableRow key={log._id} hover>
                                                    <TableCell>
                                                        <Typography variant="subtitle2" fontWeight="bold">
                                                            {staffMembers.find(u => u.userID === log.userID)?.name || "Unknown"}
                                                        </Typography>
                                                        <Typography variant="caption" color="textSecondary">ID: {log.userID}</Typography>
                                                    </TableCell>
                                                    <TableCell>{log.startTime ? new Date(log.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }) : "-"}</TableCell>
                                                    <TableCell>{log.endTime ? new Date(log.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }) : "Active"}</TableCell>
                                                    <TableCell align="center">
                                                        <Chip label={calculateHours(log.startTime, log.endTime)} size="small" variant="outlined" />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <IconButton color="primary" onClick={() => handleOpenModal(log)}><EditIcon fontSize="small" /></IconButton>
                                                        <IconButton color="error" onClick={() => handleDelete(log._id)}><DeleteIcon fontSize="small" /></IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                            {logs.length === 0 && (
                                <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'background.paper', borderRadius: 2, border: '1px dashed #ccc' }}>
                                    <HistoryIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                                    <Typography color="textSecondary">No time punches for this date.</Typography>
                                </Box>
                            )}
                        </>
                    )}
                </Grid>

                <Grid item xs={12} lg={3}>
                    <Stack spacing={3}>
                        <Box>
                            <Typography variant="h6" fontWeight="bold" gutterBottom>Calendar View</Typography>
                            <SimpleCalendar
                                selectedDate={filters.dateFrom}
                                onDateChange={(newDate) => setFilters({ ...filters, dateFrom: newDate })}
                            />
                        </Box>

                        <Card variant="outlined" sx={{ borderRadius: 2, bgcolor: 'primary.main', color: 'white' }}>
                            <CardContent>
                                <Typography variant="subtitle2" gutterBottom>Daily Summary</Typography>
                                <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.2)' }} />
                                <Stack spacing={1}>
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography variant="caption">Staff Count:</Typography>
                                        <Typography variant="subtitle2" fontWeight="bold">{dailySummary.staffCount}</Typography>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography variant="caption">Total Hours:</Typography>
                                        <Typography variant="subtitle2" fontWeight="bold">{dailySummary.totalHours}</Typography>
                                    </Box>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Stack>
                </Grid>
            </Grid>

            {/* Edit Dialog */}
            <Dialog open={openModal} onClose={handleClose} fullWidth maxWidth="sm" fullScreen={isMobile}>
                <DialogTitle sx={{ fontWeight: 'bold' }}>
                    {editMode ? "Correct Time Punch" : "Add Missing Punch"}
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <FormControl fullWidth size="small" disabled={editMode}>
                                <InputLabel>Staff Member</InputLabel>
                                <Select
                                    name="userID"
                                    value={formData.userID}
                                    label="Staff Member"
                                    onChange={(e) => {
                                        const user = staffMembers.find(u => u.userID === e.target.value);
                                        setFormData({ ...formData, userID: e.target.value, userName: user?.name || "" });
                                    }}
                                >
                                    {staffMembers.map(user => (
                                        <MenuItem key={user.userID} value={user.userID}>{user.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField 
                                fullWidth 
                                size="small" 
                                label="Clock In Time" 
                                name="startTime" 
                                type="datetime-local" 
                                InputLabelProps={{ shrink: true }} 
                                value={formData.startTime} 
                                onChange={handleInputChange} 
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField 
                                fullWidth 
                                size="small" 
                                label="Clock Out Time" 
                                name="endTime" 
                                type="datetime-local" 
                                InputLabelProps={{ shrink: true }} 
                                value={formData.endTime} 
                                onChange={handleInputChange} 
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField 
                                fullWidth 
                                size="small" 
                                label="Admin Note" 
                                name="note" 
                                multiline 
                                rows={3} 
                                placeholder="Reason for change..." 
                                value={formData.note} 
                                onChange={handleInputChange} 
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleClose} color="inherit">Cancel</Button>
                    <Button onClick={handleSave} variant="contained" color="primary" disabled={loading}>
                        {loading ? <CircularProgress size={20} /> : "Save Record"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default TimePunchManager;